import React, { useEffect, useState, useCallback, useMemo } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Button, Dropdown, Icon } from 'semantic-ui-react';
import { useSelector, useDispatch } from 'react-redux';
import { SIGNATURE_FILE_TYPE } from '../../../../HelperMethods/constants';
import { getFileURL, uploadFile } from '../../../../service/uploadDownloadService';
import logUtils from '../../../../HelperMethods/LogUtils';
import { getFileName, base64ToArrayBuffer } from '../../../../HelperMethods/UtilityFunctions';
import { useDownloadButtonClicked } from '../../../../HelperMethods/CommonHooks';
import { updateSignature } from '../../../../actions/appActions';
import ManualUploadDropdownModal from './ManualUploadDropdownModal';
import TypeNameDropdownModal from './TypeNameDropdownModal';
import { actionRecordUtils } from '../../../../HelperMethods/LocalStorageUtils';

const toDataUrl = async (url) => {
  let res = await fetch(url);
  res = await res.blob();
  return new Promise((resolve) => {
    const fr = new FileReader();
    fr.onload = (e) => resolve(e.target.result);
    fr.readAsDataURL(res);
  });
};

const createCanvasForSignature = (text) => {
  const canvas = document.createElement('canvas');
  const canvasContext = canvas.getContext('2d');
  const fontSelected = 'Allura';
  const { width: currentCanvasWidth, height: currentCanvasHeight } = { width: 300, height: 60 };
  canvas.width = currentCanvasWidth;
  canvas.height = currentCanvasHeight;
  canvasContext.clearRect(0, 0, currentCanvasWidth, currentCanvasHeight);
  canvasContext.fillStyle = 'white';
  canvasContext.fillRect(0, 0, currentCanvasWidth, currentCanvasHeight);
  canvasContext.font = `30px ${fontSelected}`;
  canvasContext.textAlign = 'center';
  canvasContext.fillStyle = 'black';
  canvasContext.fillText(text, currentCanvasWidth / 2, 30);

  canvasContext.font = '12px JoynFontBook';
  canvasContext.fillStyle = 'black';
  canvasContext.textAlign = 'right';
  canvasContext.fillText(moment().format('MM-DD-YYYY h:mmA'), currentCanvasWidth - 5, currentCanvasHeight - 5);

  const dataUrl = canvas.toDataURL('image/png');
  const base64String = dataUrl.split(',')[1];
  const arrayBuffer = base64ToArrayBuffer(base64String);
  return arrayBuffer;
};
const ESignatureComponent = React.memo(function ESignatureComponent({
  fieldName,
  onChange,
  err,
  disabled,
  uploadedFileObj,
  showESignOption = true,
  showManualUploadOption = true,
  isRequired = false,
  isCreate = false,
  isRequiredOnCreation = false,
  assigneeDetails = [],
}) {
  const [assignee] = assigneeDetails;
  const loggedInUser = useSelector((state) => state.appReducer.loggedInUser);
  const [typeNameDropdownModalOpen, setTypeNameDropdownModalOpen] = useState(false);
  const [eSignMessage, setESignMessage] = useState(false);
  const [manualUploadDropdownModalOpen, setManualUploadDropdownModalOpen] = useState(false);
  const dispatch = useDispatch();

  const eSign = useCallback(async (text) => {
    setESignMessage(true);
    const arrayBuffer = createCanvasForSignature(text);
    try {
      const uploadObj = await uploadFile({ fileName: 'type-name.png', fileType: 'image/png', arrayBuffer });
      setESignMessage(false);
      onChange({ ...uploadObj, fileType: SIGNATURE_FILE_TYPE.IMAGE });
      dispatch(updateSignature(uploadObj));
    } catch (e) {
      setESignMessage(false);
      logUtils.logError(e, 'Could not eSign ');
    }
  });

  const isActionRecordPresent = useMemo(() => {
    return actionRecordUtils.GET_ACTION_RECORD();
  }, []);

  return (
    <>
      <label>
        {fieldName}
        {!isCreate && isRequired && <span className="mandatory-resolve-field">*</span>}
        {isCreate ? (
          isRequiredOnCreation ? (
            <span className="mandatory-field">*</span>
          ) : isRequired ? (
            <span className="mandatory-resolve-field">*</span>
          ) : (
            ''
          )
        ) : (
          ''
        )}
      </label>
      <br />
      <Dropdown
        direction="right"
        disabled={disabled}
        closeOnChange
        trigger={
          <button
            type="button"
            style={{
              border: '0.5px solid #424f62',
              borderRadius: '4px',
              padding: '10px',
              color: '#424f62',
              background: 'transparent',
            }}
            className="no-print"
          >
            <Icon name="add" style={{ marginRight: '8px' }} />
            Add Signature
            <Icon name="dropdown" style={{ marginLeft: '8px' }} />
          </button>
        }
        icon={null}
      >
        <Dropdown.Menu style={{ minWidth: '100%' }}>
          {/* <FromSavedDropdownItem loggedInUser={loggedInUser} onSelected={onChange} /> */}
          {/* <UploadImageDropdownItem loggedInUser={loggedInUser} onFileUploaded={onChange} /> */}
          {/* <Dropdown.Item
            onClick={() => {
              setTypeNameDropdownModalOpen(true);
            }}
          >
            Type Your Name
          </Dropdown.Item> */}
          {showESignOption && (
            <>
              {!isActionRecordPresent && (
                <Dropdown.Item
                  onClick={() => {
                    eSign(`${loggedInUser.firstName} ${loggedInUser.lastName}`);
                  }}
                >
                  eSign <span style={{ fontFamily: 'Allura' }} />
                  {/* Add dummy span to load the Allure font */}
                </Dropdown.Item>
              )}
              {!isActionRecordPresent && assigneeDetails?.length ? (
                <Dropdown.Item
                  onClick={() => {
                    eSign(`${assignee?.firstName} ${assignee?.lastName}`);
                  }}
                >
                  Sign For Assignee <span style={{ fontFamily: 'Allura' }} />
                  {/* Add dummy span to load the Allure font */}
                </Dropdown.Item>
              ) : null}
              <Dropdown.Item
                onClick={() => {
                  setTypeNameDropdownModalOpen(true);
                }}
              >
                Manual Sign
              </Dropdown.Item>
            </>
          )}

          {showManualUploadOption && (
            <Dropdown.Item
              onClick={() => {
                setManualUploadDropdownModalOpen(true);
              }}
            >
              Sign via Paper
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
      {eSignMessage && <span style={{ marginLeft: '10px' }}>Generating eSignature ...</span>}
      <ManualUploadDropdownModal
        setOpenModal={setManualUploadDropdownModalOpen}
        openModal={manualUploadDropdownModalOpen}
        onFileUploaded={onChange}
        loggedInUser={loggedInUser}
      />
      <TypeNameDropdownModal
        setOpenModal={setTypeNameDropdownModalOpen}
        openModal={typeNameDropdownModalOpen}
        onFileUploaded={onChange}
        loggedInUser={loggedInUser}
      />
      <AttachmentFilePreview uploadedFileObj={uploadedFileObj} fieldName={fieldName} />
      {err && <div className="errorText">* {err}</div>}
    </>
  );
});

ESignatureComponent.propTypes = {
  fieldName: PropTypes.string.isRequired,
  uploadedFileObj: PropTypes.shape({
    key: PropTypes.string.isRequired,
    bucket: PropTypes.string.isRequired,
    region: PropTypes.string.isRequired,
  }),
  onChange: PropTypes.func.isRequired,
  err: PropTypes.string,
  disabled: PropTypes.bool,
  showESignOption: PropTypes.bool,
  showManualUploadOption: PropTypes.bool,
  isRequired: PropTypes.bool,
  assigneeDetails: PropTypes.array,
};

ESignatureComponent.defaultProps = {
  err: null,
  disabled: false,
  uploadedFileObj: null,
};

function AttachmentFilePreview({ uploadedFileObj, fieldName }) {
  if (!uploadedFileObj || !uploadedFileObj.key) return null;
  const { fileType = SIGNATURE_FILE_TYPE.IMAGE } = uploadedFileObj;
  return (
    <>
      {fileType === SIGNATURE_FILE_TYPE.IMAGE && (
        <SignatureImage uploadedImageObj={uploadedFileObj} imageAlt={fieldName} />
      )}
      {fileType === SIGNATURE_FILE_TYPE.PDF && <SignaturePDF uploadedPDFObj={uploadedFileObj} />}
    </>
  );
}

AttachmentFilePreview.propTypes = {
  uploadedFileObj: PropTypes.shape({
    key: PropTypes.string.isRequired,
    bucket: PropTypes.string.isRequired,
    region: PropTypes.string.isRequired,
    fileType: PropTypes.string,
  }),
  fieldName: PropTypes.string.isRequired,
};

AttachmentFilePreview.defaultProps = {
  uploadedFileObj: null,
};

function SignaturePDF({ uploadedPDFObj }) {
  const onDownloadButtonClicked = useDownloadButtonClicked(uploadedPDFObj);
  return (
    <div>
      <div>Signed via Paper - see attachment</div>
      {uploadedPDFObj.fileName || getFileName(uploadedPDFObj.key)}&nbsp;&nbsp;
      <Button type="button" style={{ marginBottom: '16px' }} icon="cloud download" onClick={onDownloadButtonClicked} />
    </div>
  );
}
SignaturePDF.propTypes = {
  uploadedPDFObj: PropTypes.shape({
    key: PropTypes.string.isRequired,
    bucket: PropTypes.string.isRequired,
    region: PropTypes.string.isRequired,
    fileName: PropTypes.string,
  }).isRequired,
};

function SignatureImage({ uploadedImageObj, imageAlt }) {
  const [image, setImage] = useState({
    isLoading: false,
    url: null,
  });
  useEffect(() => {
    if (uploadedImageObj && uploadedImageObj.key) {
      setImage({ isLoading: true, url: null });
      getFileURL(uploadedImageObj)
        .then(async (signedUrl) => {
          const dataUrl = await toDataUrl(signedUrl);
          setImage({ isLoading: false, url: dataUrl });
        })
        .catch((e) => {
          logUtils.logError(e, 'Could not load signature image');
          setImage({ isLoading: false, url: null });
        });
    }
    return () => {
      setImage({ isLoading: false, url: null });
    };
  }, [uploadedImageObj.key]);
  if (image.isLoading) return <div style={{ margin: '12px 24px', fontFamily: 'JoynFontBook' }}>Loading Image...</div>;
  return image.url ? (
    <figure style={{ width: '300px', height: '60px' }}>
      <img alt={imageAlt} src={image.url} style={{ width: '100%', height: '100%' }} height="100" />
    </figure>
  ) : (
    <div style={{ margin: '12px 24px', fontFamily: 'JoynFontBook' }}>Could not load Image</div>
  );
}

SignatureImage.propTypes = {
  uploadedImageObj: PropTypes.shape({
    key: PropTypes.string.isRequired,
    bucket: PropTypes.string.isRequired,
    region: PropTypes.string.isRequired,
  }).isRequired,
  imageAlt: PropTypes.string.isRequired,
};

export default React.memo(ESignatureComponent);
